import React, { useEffect, useMemo, useState } from "react";
import { GlobalStyles } from "../../constants/styles";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

export default function DualListSelector({
                                             title,
                                             description,
                                             leftTitle,
                                             rightTitle,
                                             items = [],
                                             selected = [],
                                             getItemLabel = (item) => item.name,
                                             onChange = () => {},
                                         }) {
    const colors = useThemeColors();
    const { t } = useTranslation();

    const resolvedTitle =
        title ?? t("dualListSelector.defaults.title");
    const resolvedDescription =
        description ?? t("dualListSelector.defaults.description");
    const resolvedLeftTitle =
        leftTitle ?? t("dualListSelector.defaults.leftTitle");
    const resolvedRightTitle =
        rightTitle ?? t("dualListSelector.defaults.rightTitle");

    const [availableItems, setAvailableItems] = useState(
        (items || []).filter((item) => !(selected || []).find((sel) => sel.id === item.id))
    );
    const [selectedItems, setSelectedItems] = useState(selected || []);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        const safeSelected = Array.isArray(selected) ? selected : [];
        const safeItems = Array.isArray(items) ? items : [];

        setSelectedItems(safeSelected);
        setAvailableItems(
            safeItems.filter((item) => !safeSelected.find((sel) => sel.id === item.id))
        );
    }, [items, selected]);

    const ui = useMemo(() => {
        const primary =
            colors.primary ||
            colors.primaryColor ||
            colors.canary ||
            GlobalStyles.colors.primary ||
            GlobalStyles.colors.canary ||
            GlobalStyles.colors.woodsmoke ||
            "#00adff";

        return {
            wrap: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(primary, 0.50)},
                    ${alphaFromHex(primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary || "#ffffff",
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: colors.textOnPrimary || "#14151a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary || "#14151a",
            },
            body: { padding: 14 },
            grid: {
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
            },
            pane: {
                borderRadius: 14,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                overflow: "hidden",
            },
            paneHeader: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: "10px 12px",
                background: "rgba(0,0,0,0.02)",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            paneTitle: {
                fontSize: 13,
                fontWeight: 900,
                color: "#14151a",
                margin: 0,
            },
            paneCount: {
                fontSize: 12,
                fontWeight: 900,
                padding: "5px 10px",
                borderRadius: 999,
                background: colors.primary || primary,
                border: "1px solid rgba(0,0,0,0.08)",
                color: colors.textOnPrimary || "#ffffff",
            },
            paneBody: { padding: 12 },
            search: {
                width: "100%",
                borderRadius: 12,
                padding: "10px 12px",
                border: "1px solid rgba(0,0,0,0.10)",
                outline: "none",
                fontWeight: 700,
            },
            list: {
                marginTop: 10,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "#fff",
                overflow: "hidden",
            },
            listScroll: {
                maxHeight: 300,
                overflowY: "auto",
            },
            row: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: "10px 12px",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            label: {
                fontWeight: 800,
                color: "#14151a",
                fontSize: 13.5,
                minWidth: 0,
            },
            labelSub: {
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
                fontSize: 12.5,
            },
            btn: {
                borderRadius: 12,
                padding: "8px 10px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                whiteSpace: "nowrap",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
            btnDanger: {
                background: "rgba(220,53,69,0.08)",
                border: "1px solid rgba(220,53,69,0.25)",
                color: "#dc3545",
            },
            empty: {
                padding: 12,
                color: "rgba(20,21,26,0.55)",
                fontWeight: 700,
                fontSize: 12.5,
            },
            hint: {
                marginTop: 10,
                color: "rgba(20,21,26,0.55)",
                fontWeight: 700,
                fontSize: 12.5,
            },
        };
    }, [colors]);

    const filteredAvailable = availableItems.filter((item) =>
        String(getItemLabel(item) || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
    );

    const selectItem = (item) => {
        const updatedSelected = [...selectedItems, item];
        const updatedAvailable = availableItems.filter((avail) => avail.id !== item.id);

        setSelectedItems(updatedSelected);
        setAvailableItems(updatedAvailable);
        onChange(updatedSelected);
    };

    const removeItem = (item) => {
        const updatedSelected = selectedItems.filter((sel) => sel.id !== item.id);
        const updatedAvailable = [...availableItems, item];

        setSelectedItems(updatedSelected);
        setAvailableItems(updatedAvailable);
        onChange(updatedSelected);
    };

    return (
        <>
            <div id="kbola-dual-list" style={ui.wrap}>
                <div style={ui.header}>
                    <div style={ui.titleWrap}>
                        <span style={ui.badge}>{items?.length || 0}</span>
                        <div style={{ minWidth: 0 }}>
                            <h3 style={ui.title}>{resolvedTitle}</h3>
                            <p style={ui.sub}>{resolvedDescription}</p>
                        </div>
                    </div>
                </div>

                <div style={ui.body}>
                    <div style={ui.grid} className="kbola-dual-grid">
                        <div style={ui.pane}>
                            <div style={ui.paneHeader}>
                                <p style={ui.paneTitle}>{resolvedLeftTitle}</p>
                                <span style={ui.paneCount}>{filteredAvailable.length}</span>
                            </div>

                            <div style={ui.paneBody}>
                                <input
                                    type="text"
                                    placeholder={t("dualListSelector.search.placeholder")}
                                    style={ui.search}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />

                                <div style={ui.list}>
                                    <div style={ui.listScroll}>
                                        {filteredAvailable.length > 0 ? (
                                            filteredAvailable.map((item, idx) => (
                                                <div
                                                    key={item.id}
                                                    style={{
                                                        ...ui.row,
                                                        borderBottom:
                                                            idx === filteredAvailable.length - 1
                                                                ? "none"
                                                                : "1px solid rgba(0,0,0,0.06)",
                                                    }}
                                                >
                                                    <div style={{ minWidth: 0 }}>
                                                        <div style={ui.label}>{getItemLabel(item)}</div>
                                                        <div style={ui.labelSub}>
                                                            {t("dualListSelector.labels.available")}
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={() => selectItem(item)}
                                                        style={{ ...ui.btn, ...ui.btnPrimary }}
                                                        className="kbola-dual-btn kbola-dual-btn-primary"
                                                        title={t("dualListSelector.titles.add")}
                                                    >
                                                        {t("dualListSelector.buttons.add")}
                                                        <i className="fas fa-chevron-right" />
                                                    </button>
                                                </div>
                                            ))
                                        ) : (
                                            <div style={ui.empty}>
                                                {t("dualListSelector.empty.noItemsFound")}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div style={ui.hint}>
                                    {t("dualListSelector.hints.searchByVenueOrField")}
                                </div>
                            </div>
                        </div>

                        <div style={ui.pane}>
                            <div style={ui.paneHeader}>
                                <p style={ui.paneTitle}>{resolvedRightTitle}</p>
                                <span style={ui.paneCount}>{selectedItems.length}</span>
                            </div>

                            <div style={ui.paneBody}>
                                <div style={ui.list}>
                                    <div style={ui.listScroll}>
                                        {selectedItems.length > 0 ? (
                                            selectedItems.map((item, idx) => (
                                                <div
                                                    key={item.id}
                                                    style={{
                                                        ...ui.row,
                                                        borderBottom:
                                                            idx === selectedItems.length - 1
                                                                ? "none"
                                                                : "1px solid rgba(0,0,0,0.06)",
                                                    }}
                                                >
                                                    <div style={{ minWidth: 0 }}>
                                                        <div style={ui.label}>{getItemLabel(item)}</div>
                                                        <div style={ui.labelSub}>
                                                            {t("dualListSelector.labels.selected")}
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={() => removeItem(item)}
                                                        style={{ ...ui.btn, ...ui.btnDanger }}
                                                        className="kbola-dual-btn kbola-dual-btn-danger"
                                                        title={t("dualListSelector.titles.remove")}
                                                    >
                                                        <i className="fas fa-chevron-left" />
                                                        {t("dualListSelector.buttons.remove")}
                                                    </button>
                                                </div>
                                            ))
                                        ) : (
                                            <div style={ui.empty}>
                                                {t("dualListSelector.empty.noItemsSelected")}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div style={ui.hint}>
                                    {t("dualListSelector.hints.selectedItemsUsedForStage")}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                #kbola-dual-list input:focus,
                #kbola-dual-list button:focus {
                  outline: none;
                  box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                }

                #kbola-dual-list .kbola-dual-btn:hover.kbola-dual-btn-primary {
                  filter: brightness(0.98);
                  transform: translateY(-1px);
                }

                #kbola-dual-list .kbola-dual-btn:hover.kbola-dual-btn-danger {
                  background: rgba(220,53,69,0.12) !important;
                  transform: translateY(-1px);
                }

                #kbola-dual-list .kbola-dual-btn {
                  transition: transform 120ms ease, filter 120ms ease, background 120ms ease;
                }

                @media (max-width: 991px) {
                  #kbola-dual-list .kbola-dual-grid {
                    grid-template-columns: 1fr !important;
                  }
                }
            `}</style>
        </>
    );
}
